import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { rutasAPI, vehiculosAPI, ticketsAPI } from '../../lib/apiRoutes';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';

const AdminRutas = () => {
  const navigate = useNavigate();
  const [rutas, setRutas] = useState([]);
  const [vehiculos, setVehiculos] = useState([]);
  const [allTickets, setAllTickets] = useState([]);
  const [loading, setLoading] = useState(false);

  // Manual create modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedTickets, setSelectedTickets] = useState([]);
  const [vehiculoSearch, setVehiculoSearch] = useState('');
  const [showVehiculoDropdown, setShowVehiculoDropdown] = useState(false);
  const [showAllVehicles, setShowAllVehicles] = useState(false);
  const [ticketDateFilter, setTicketDateFilter] = useState(new Date().toISOString().split('T')[0]);
  const [ticketZoneFilter, setTicketZoneFilter] = useState('');
  const [formData, setFormData] = useState({ fecha: new Date().toISOString().split('T')[0], vehiculo_id: '', conductor: '' });

  // ─── Optimize modal state ────────────────────────────────
  const [showOptimizeModal, setShowOptimizeModal] = useState(false);
  const [optStep, setOptStep] = useState(1); // 1=config, 2=results
  const [optimizing, setOptimizing] = useState(false);
  const [previewTickets, setPreviewTickets] = useState([]);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [optimizationResults, setOptimizationResults] = useState([]); // one per truck
  const [creatingRoutes, setCreatingRoutes] = useState(false);

  const [optConfig, setOptConfig] = useState({
    fecha_desde: new Date().toISOString().split('T')[0],
    fecha_hasta: new Date().toISOString().split('T')[0],
    zona: '',
    jornada_horas: 7.0,
    tiempo_parada_min: 5,
  });

  // Trucks to use in optimization
  const [optTrucks, setOptTrucks] = useState([]);
  // { vehiculo: {...}, max_items: 25, conductor: '' }

  // ─── Data loading ────────────────────────────────────────
  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      const [rutasRes, vehiculosRes, ticketsRes] = await Promise.all([
        rutasAPI.list(),
        vehiculosAPI.list(),
        ticketsAPI.list({ fecha_desde: today, limit: 500 })
      ]);
      setRutas(rutasRes.data.rutas || []);
      setVehiculos(vehiculosRes.data.vehiculos || []);
      setAllTickets(ticketsRes.data.tickets || []);
    } catch (error) {
      toast.error('Error cargando datos');
    } finally {
      setLoading(false);
    }
  };

  // ─── Derived data ────────────────────────────────────────
  const favoriteVehicles = useMemo(() => vehiculos.filter(v => v.favorito), [vehiculos]);

  const availableTickets = useMemo(() => {
    return allTickets.filter(t => {
      if (t.ruta_asignada) return false;
      if (!['Notificada', 'Asignada'].includes(t.estado)) return false;
      if (ticketDateFilter) {
        const d = new Date(t.fecha_preferida_retirada).toISOString().split('T')[0];
        if (d !== ticketDateFilter) return false;
      }
      if (ticketZoneFilter && t.lugar !== ticketZoneFilter) return false;
      return true;
    });
  }, [allTickets, ticketDateFilter, ticketZoneFilter]);

  const displayVehicles = useMemo(() => {
    const src = showAllVehicles ? vehiculos : favoriteVehicles;
    if (!vehiculoSearch) return src;
    const s = vehiculoSearch.toLowerCase();
    return src.filter(v =>
      v.matricula?.toLowerCase().includes(s) ||
      v.tipo?.toLowerCase().includes(s) ||
      v.marca?.toLowerCase().includes(s) ||
      v.modelo?.toLowerCase().includes(s)
    );
  }, [vehiculos, favoriteVehicles, vehiculoSearch, showAllVehicles]);

  // IDs already added as trucks
  const addedTruckIds = useMemo(() => new Set(optTrucks.map(t => t.vehiculo._id)), [optTrucks]);

  // Available favorites not yet added
  const availableFavVehicles = useMemo(() =>
    favoriteVehicles.filter(v => !addedTruckIds.has(v._id)),
    [favoriteVehicles, addedTruckIds]
  );

  // ─── Preview tickets for optimization ────────────────────
  const loadPreviewTickets = useCallback(async () => {
    setLoadingPreview(true);
    try {
      // Use dedicated endpoint that returns ALL eligible tickets (pending, not assigned)
      const params = {};
      if (optConfig.zona) params.zona = optConfig.zona;
      params.limit = 2000;
      
      const res = await api.get('/api/tickets/elegibles', { params });
      setPreviewTickets(res.data.tickets || []);
    } catch (e) {
      toast.error('Error cargando tickets');
      setPreviewTickets([]);
    } finally {
      setLoadingPreview(false);
    }
  }, [optConfig.zona]);

  // Auto-load preview when zone changes or modal opens
  useEffect(() => {
    if (showOptimizeModal && optStep === 1) {
      const timer = setTimeout(loadPreviewTickets, 400);
      return () => clearTimeout(timer);
    }
  }, [optConfig.zona, showOptimizeModal, optStep, loadPreviewTickets]);

  // ─── Optimization flow ───────────────────────────────────
  const totalTruckCapacity = useMemo(() =>
    optTrucks.reduce((sum, t) => sum + (t.max_items || 0), 0),
    [optTrucks]
  );

  const totalTicketItems = useMemo(() =>
    previewTickets.reduce((sum, t) => sum + (t.items?.length || 1), 0),
    [previewTickets]
  );

  const addTruck = (vehiculo) => {
    setOptTrucks(prev => [...prev, { vehiculo, max_items: 25, conductor: '' }]);
  };

  const removeTruck = (idx) => {
    setOptTrucks(prev => prev.filter((_, i) => i !== idx));
  };

  const updateTruck = (idx, field, value) => {
    setOptTrucks(prev => prev.map((t, i) => i === idx ? { ...t, [field]: value } : t));
  };

  const handleOptimize = async () => {
    if (optTrucks.length === 0) { toast.error('Añada al menos un camión'); return; }
    if (previewTickets.length < 2) { toast.error('Se necesitan al menos 2 tickets para optimizar'); return; }

    setOptimizing(true);
    try {
      // Split tickets among trucks proportionally by max_items capacity
      const totalCap = optTrucks.reduce((s, t) => s + t.max_items, 0);
      // Limit total tickets to total capacity
      const ticketsToAssign = previewTickets.slice(0, Math.min(previewTickets.length, totalCap));
      let remaining = [...ticketsToAssign];
      const truckTickets = [];

      for (let i = 0; i < optTrucks.length; i++) {
        const truck = optTrucks[i];
        const count = i === optTrucks.length - 1
          ? remaining.length
          : Math.min(truck.max_items, remaining.length);
        truckTickets.push(remaining.splice(0, count));
      }

      // Optimize each truck's route via AI
      const results = [];
      for (let i = 0; i < optTrucks.length; i++) {
        const tickets = truckTickets[i];
        if (tickets.length === 0) {
          results.push({ truck: optTrucks[i], tickets: [], optimization: { razonamiento: 'Sin tickets asignados.' }, success: true });
          continue;
        }
        if (tickets.length === 1) {
          results.push({
            truck: optTrucks[i], tickets, success: true,
            optimization: { secuencia_optima: [tickets[0].ticket_id], tiempo_estimado_minutos: 30, distancia_estimada_km: 5, razonamiento: 'Ruta directa con 1 recogida.' }
          });
          continue;
        }

        try {
          // Send ticket IDs to optimize endpoint
          const response = await api.post('/api/rutas/optimize-tickets', {
            ticket_ids: tickets.map(t => t.ticket_id),
            jornada_horas: optConfig.jornada_horas,
            tiempo_parada_min: optConfig.tiempo_parada_min
          });

          const ticketMap = new Map(tickets.map(t => [t.ticket_id, t]));
          const orderedTickets = (response.data.optimization?.secuencia_optima || [])
            .filter(id => ticketMap.has(id))
            .map(id => ticketMap.get(id));
          // Add any missing
          const orderedIds = new Set(orderedTickets.map(t => t.ticket_id));
          tickets.forEach(t => { if (!orderedIds.has(t.ticket_id)) orderedTickets.push(t); });

          results.push({
            truck: optTrucks[i], tickets: orderedTickets,
            optimization: response.data.optimization || {},
            success: response.data.success !== false
          });
        } catch (err) {
          // Fallback: original order
          results.push({
            truck: optTrucks[i], tickets, success: false,
            optimization: { secuencia_optima: tickets.map(t => t.ticket_id), tiempo_estimado_minutos: tickets.length * 15, distancia_estimada_km: tickets.length * 3, razonamiento: 'Optimización fallback: orden por fecha.' }
          });
        }
      }

      setOptimizationResults(results);
      setOptStep(2);
      toast.success(`Optimización completada para ${results.length} camión(es)`);
    } catch (err) {
      toast.error('Error durante la optimización');
    } finally {
      setOptimizing(false);
    }
  };

  const handleCreateAllRoutes = async () => {
    setCreatingRoutes(true);
    try {
      let created = 0;
      for (const result of optimizationResults) {
        if (result.tickets.length === 0) continue;
        const ticketIds = result.tickets.map(t => t.ticket_id);
        await rutasAPI.createOptimized({
          fecha: optConfig.fecha_desde,
          vehiculo_id: result.truck.vehiculo._id,
          ticket_ids: ticketIds,
          conductor: result.truck.conductor || null,
          optimization_result: result.optimization
        });
        created++;
      }
      toast.success(`${created} ruta(s) creadas exitosamente`);
      closeOptimizeModal();
      loadData();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'Error creando rutas');
    } finally {
      setCreatingRoutes(false);
    }
  };

  const closeOptimizeModal = () => {
    setShowOptimizeModal(false);
    setOptStep(1);
    setOptimizationResults([]);
    setPreviewTickets([]);
    setOptTrucks([]);
  };

  // ─── Manual route helpers ────────────────────────────────
  const toggleTicket = (ticketId) => {
    setSelectedTickets(prev => prev.includes(ticketId) ? prev.filter(id => id !== ticketId) : [...prev, ticketId]);
  };

  const selectVehiculo = (vehiculo) => {
    setFormData({ ...formData, vehiculo_id: vehiculo._id });
    setVehiculoSearch(`${vehiculo.matricula} - ${vehiculo.tipo || ''}`);
    setShowVehiculoDropdown(false);
  };

  const handleCreateRuta = async () => {
    if (!formData.vehiculo_id || selectedTickets.length === 0) { toast.error('Seleccione vehículo y tickets'); return; }
    try {
      await rutasAPI.create({ fecha: new Date(formData.fecha).toISOString(), vehiculo_id: formData.vehiculo_id, tickets: selectedTickets, conductor: formData.conductor });
      toast.success('Ruta creada');
      setShowCreateModal(false);
      setSelectedTickets([]);
      loadData();
    } catch (error) { toast.error('Error creando ruta'); }
  };

  const handlePredefinedRoute = (zona) => {
    setTicketZoneFilter(zona);
    setTicketDateFilter(new Date().toISOString().split('T')[0]);
    setSelectedTickets([]);
    setFormData({ ...formData, fecha: new Date().toISOString().split('T')[0] });
    setShowCreateModal(true);
  };

  // ─── Vehicle selector component ──────────────────────────
  const VehicleSelector = ({ onSelect, searchVal, onSearchChange }) => (
    <div>
      <div className="flex items-center justify-between mb-1">
        <Label>Vehículo *</Label>
        <button type="button" onClick={() => setShowAllVehicles(!showAllVehicles)}
          className="text-xs text-blue-600 hover:text-blue-800 transition-colors duration-150">
          {showAllVehicles ? 'Solo favoritos' : 'Ver todos'}
        </button>
      </div>
      {!showAllVehicles && favoriteVehicles.length === 0 && (
        <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded mb-2">No hay favoritos. Márcalos en Vehículos.</p>
      )}
      <div className="relative">
        <Input value={searchVal}
          onChange={(e) => { onSearchChange(e.target.value); setShowVehiculoDropdown(true); if (formData.vehiculo_id) setFormData({ ...formData, vehiculo_id: '' }); }}
          onFocus={() => setShowVehiculoDropdown(true)}
          placeholder={showAllVehicles ? 'Buscar matrícula...' : 'Buscar entre favoritos...'}
          data-testid="input-vehiculo-search" />
        {showVehiculoDropdown && displayVehicles.length > 0 && (
          <div className="absolute z-[100] w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
            {displayVehicles.slice(0, 15).map(v => (
              <div key={v._id} onClick={() => onSelect(v)}
                className="p-2 hover:bg-blue-50 cursor-pointer border-b last:border-b-0 text-sm">
                <span className="font-semibold">{v.favorito ? '★ ' : ''}{v.matricula}</span>
                <span className="text-gray-500 ml-2">{v.tipo || ''}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      {formData.vehiculo_id && <p className="text-xs text-green-600 mt-1">Vehículo seleccionado</p>}
    </div>
  );

  // ─── RENDER ──────────────────────────────────────────────
  return (
    <div data-testid="admin-rutas-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Gestión de Rutas</h1>
        <div className="flex gap-3">
          <Button variant="outline" onClick={() => navigate('/admin/vehiculos')}>Vehículos</Button>
          <Button onClick={() => { setShowOptimizeModal(true); setOptStep(1); setOptTrucks([]); setOptimizationResults([]); }}
            className="bg-blue-600 hover:bg-blue-700" data-testid="btn-optimize">
            Optimizar con IA
          </Button>
          <Button onClick={() => { setShowCreateModal(true); setTicketZoneFilter(''); }} data-testid="btn-new-route">
            + Nueva Ruta Manual
          </Button>
        </div>
      </div>

      {/* Predefined routes */}
      <div className="flex gap-3 mb-6">
        <Card className="flex-1 cursor-pointer hover:shadow-md transition-shadow duration-200" onClick={() => handlePredefinedRoute('Casco Antiguo')}>
          <CardContent className="pt-4 pb-4 flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-gray-900">Ruta Casco Antiguo</h3>
              <p className="text-xs text-gray-500">Tickets de Casco Antiguo</p>
            </div>
            <Button size="sm" variant="outline" data-testid="btn-ruta-casco-antiguo">Crear</Button>
          </CardContent>
        </Card>
        <Card className="flex-1 cursor-pointer hover:shadow-md transition-shadow duration-200" onClick={() => handlePredefinedRoute('Puerta Tierra')}>
          <CardContent className="pt-4 pb-4 flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-gray-900">Ruta Puerta Tierra</h3>
              <p className="text-xs text-gray-500">Tickets de Puerta Tierra</p>
            </div>
            <Button size="sm" variant="outline" data-testid="btn-ruta-puerta-tierra">Crear</Button>
          </CardContent>
        </Card>
      </div>

      {/* Routes list */}
      {loading ? (
        <div className="text-center py-8">Cargando...</div>
      ) : rutas.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No hay rutas planificadas</h3>
            <p className="text-gray-500 mb-4">Crea una ruta o usa el optimizador con IA</p>
            <Button onClick={() => setShowOptimizeModal(true)} className="bg-blue-600 hover:bg-blue-700">Optimizar con IA</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {rutas.map(ruta => (
            <Card key={ruta._id} className="hover:shadow-md transition-shadow duration-200">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    {ruta.optimizada_ia && <Badge variant="outline" className="text-purple-600 border-purple-200">IA</Badge>}
                    Ruta {new Date(ruta.fecha).toLocaleDateString('es-ES')}
                  </CardTitle>
                  <div className="flex gap-2 items-center">
                    <Badge className={
                      ruta.estado === 'Completada' ? 'bg-green-100 text-green-800' :
                      ruta.estado === 'En Proceso' ? 'bg-blue-100 text-blue-800' :
                      'bg-yellow-100 text-yellow-800'
                    }>{ruta.estado}</Badge>
                    <Button variant="outline" size="sm" onClick={() => rutasAPI.exportPDF(ruta._id)}>PDF</Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-4 gap-4 text-sm">
                  <div><span className="text-gray-500">Vehículo:</span><p>{ruta.vehiculo_info?.matricula}</p></div>
                  <div><span className="text-gray-500">Conductor:</span><p>{ruta.conductor || 'No asignado'}</p></div>
                  <div><span className="text-gray-500">Tickets:</span><p>{ruta.tickets?.length || 0} recogidas</p></div>
                  <div><span className="text-gray-500">Tipo:</span><p>{ruta.optimizada_ia ? 'IA' : 'Manual'}</p></div>
                </div>
                <Button variant="link" className="mt-3 p-0" onClick={() => navigate(`/admin/rutas/${ruta._id}`)}>Ver Detalle →</Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════
          OPTIMIZE MODAL — redesigned multi-truck flow
          ══════════════════════════════════════════════════════ */}
      {showOptimizeModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={closeOptimizeModal}>
          <div className="bg-white rounded-xl shadow-2xl max-w-5xl w-full max-h-[92vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
            {/* Modal header */}
            <div className="px-6 py-4 border-b flex items-center justify-between bg-gray-50">
              <div>
                <h2 className="text-xl font-bold text-gray-900" data-testid="optimize-modal-title">
                  Optimización de Rutas con IA
                </h2>
                <p className="text-sm text-gray-500">
                  {optStep === 1 ? 'Paso 1: Configura fecha, camiones y revisa tickets' : 'Paso 2: Resultado de la optimización'}
                </p>
              </div>
              <button onClick={closeOptimizeModal} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
            </div>

            {/* Modal body */}
            <div className="flex-1 overflow-y-auto px-6 py-5">
              {optStep === 1 && (
                <div className="space-y-6">
                  {/* ── Date & Zone ── */}
                  <div>
                    <Label className="text-sm font-semibold text-gray-700 mb-2 block">Rango de fechas y zona</Label>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label className="text-xs">Fecha Desde</Label>
                        <Input type="date" value={optConfig.fecha_desde}
                          onChange={e => setOptConfig({ ...optConfig, fecha_desde: e.target.value })} data-testid="opt-fecha-desde" />
                      </div>
                      <div>
                        <Label className="text-xs">Fecha Hasta</Label>
                        <Input type="date" value={optConfig.fecha_hasta}
                          onChange={e => setOptConfig({ ...optConfig, fecha_hasta: e.target.value })} data-testid="opt-fecha-hasta" />
                      </div>
                      <div>
                        <Label className="text-xs">Zona</Label>
                        <select value={optConfig.zona} onChange={e => setOptConfig({ ...optConfig, zona: e.target.value })}
                          className="w-full p-2 border rounded-md text-sm h-9" data-testid="opt-zona">
                          <option value="">Todas</option>
                          <option value="Casco Antiguo">Casco Antiguo</option>
                          <option value="Puerta Tierra">Puerta Tierra</option>
                        </select>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-2">
                      {[
                        { label: 'Hoy', fn: () => { const d = new Date().toISOString().split('T')[0]; setOptConfig(p => ({ ...p, fecha_desde: d, fecha_hasta: d })); } },
                        { label: '7 días', fn: () => { const t = new Date(); const n = new Date(t.getTime()+7*864e5); setOptConfig(p => ({ ...p, fecha_desde: t.toISOString().split('T')[0], fecha_hasta: n.toISOString().split('T')[0] })); } },
                      ].map(b => (
                        <Button key={b.label} type="button" variant="outline" size="sm" onClick={b.fn}>{b.label}</Button>
                      ))}
                    </div>
                  </div>

                  {/* ── Tickets preview ── */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-sm font-semibold text-gray-700">
                        Tickets disponibles
                        {!loadingPreview && <span className="font-normal text-gray-500 ml-2">
                          ({previewTickets.length} tickets · {totalTicketItems} items)
                        </span>}
                      </Label>
                      <Button variant="ghost" size="sm" onClick={loadPreviewTickets} disabled={loadingPreview}>
                        {loadingPreview ? 'Cargando...' : 'Actualizar'}
                      </Button>
                    </div>
                    <div className="border rounded-lg max-h-48 overflow-y-auto bg-white">
                      {loadingPreview ? (
                        <div className="py-6 text-center text-gray-500 text-sm">Cargando tickets...</div>
                      ) : previewTickets.length === 0 ? (
                        <div className="py-6 text-center text-gray-500 text-sm">No hay tickets pendientes para estas fechas</div>
                      ) : (
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50 sticky top-0">
                            <tr>
                              <th className="text-left px-3 py-2 font-medium text-gray-600">Ticket</th>
                              <th className="text-left px-3 py-2 font-medium text-gray-600">Ciudadano</th>
                              <th className="text-left px-3 py-2 font-medium text-gray-600">Dirección</th>
                              <th className="text-left px-3 py-2 font-medium text-gray-600">Zona</th>
                              <th className="text-center px-3 py-2 font-medium text-gray-600">Items</th>
                              <th className="text-left px-3 py-2 font-medium text-gray-600">Fecha</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y">
                            {previewTickets.map(t => (
                              <tr key={t.ticket_id} className="hover:bg-gray-50">
                                <td className="px-3 py-2 font-medium text-blue-700">{t.ticket_id}</td>
                                <td className="px-3 py-2 text-gray-700 truncate max-w-[140px]">{t.nombre_ciudadano}</td>
                                <td className="px-3 py-2 text-gray-600 truncate max-w-[180px]">{t.direccion}</td>
                                <td className="px-3 py-2"><Badge variant="outline" className="text-xs">{t.lugar}</Badge></td>
                                <td className="px-3 py-2 text-center">{t.items?.length || 0}</td>
                                <td className="px-3 py-2 text-gray-500">{new Date(t.fecha_preferida_retirada).toLocaleDateString('es-ES')}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  </div>

                  {/* ── Trucks ── */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-sm font-semibold text-gray-700">
                        Camiones asignados
                        <span className="font-normal text-gray-500 ml-2">({optTrucks.length})</span>
                      </Label>
                    </div>

                    {/* Added trucks */}
                    {optTrucks.length > 0 && (
                      <div className="space-y-3 mb-4">
                        {optTrucks.map((truck, idx) => (
                          <div key={idx} className="border rounded-lg p-3 bg-white flex items-center gap-4" data-testid={`opt-truck-${idx}`}>
                            <div className="flex-shrink-0">
                              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center font-bold text-blue-700">
                                {idx + 1}
                              </div>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-sm">{truck.vehiculo.matricula}</p>
                              <p className="text-xs text-gray-500">{truck.vehiculo.tipo || truck.vehiculo.marca || ''}</p>
                            </div>
                            <div className="w-32">
                              <Label className="text-xs">Max items</Label>
                              <Input type="number" value={truck.max_items} min={1} max={100}
                                onChange={e => updateTruck(idx, 'max_items', parseInt(e.target.value) || 1)}
                                className="h-8 text-sm" data-testid={`opt-truck-items-${idx}`} />
                            </div>
                            <div className="w-40">
                              <Label className="text-xs">Conductor</Label>
                              <Input value={truck.conductor}
                                onChange={e => updateTruck(idx, 'conductor', e.target.value)}
                                placeholder="Nombre..."
                                className="h-8 text-sm" />
                            </div>
                            <button onClick={() => removeTruck(idx)} className="text-red-400 hover:text-red-600 text-lg flex-shrink-0" title="Quitar">&times;</button>
                          </div>
                        ))}
                        {/* Capacity bar */}
                        <div className="bg-gray-50 rounded-lg p-3 text-sm">
                          <div className="flex justify-between mb-1">
                            <span className="text-gray-600">Capacidad total: <strong>{totalTruckCapacity} items</strong></span>
                            <span className={totalTruckCapacity >= totalTicketItems ? 'text-green-600' : 'text-amber-600'}>
                              {totalTicketItems} items en tickets
                              {totalTruckCapacity >= totalTicketItems ? ' — Capacidad suficiente' : ' — Falta capacidad'}
                            </span>
                          </div>
                          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div className={`h-full transition-all duration-300 ${totalTruckCapacity >= totalTicketItems ? 'bg-green-500' : 'bg-amber-500'}`}
                              style={{ width: Math.min(100, totalTicketItems > 0 ? (totalTruckCapacity / totalTicketItems) * 100 : 0) + '%' }} />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Add truck buttons */}
                    {availableFavVehicles.length > 0 ? (
                      <div>
                        <p className="text-xs text-gray-500 mb-2">Añadir vehículo favorito:</p>
                        <div className="flex flex-wrap gap-2">
                          {availableFavVehicles.map(v => (
                            <Button key={v._id} variant="outline" size="sm" onClick={() => addTruck(v)}
                              className="text-sm" data-testid={`opt-add-truck-${v.matricula}`}>
                              + {v.matricula}
                              <span className="text-gray-400 ml-1 text-xs">{v.tipo?.split(' ')[0] || ''}</span>
                            </Button>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <p className="text-xs text-gray-500">
                        {favoriteVehicles.length === 0
                          ? 'No hay vehículos favoritos. Márcalos en Gestión de Vehículos.'
                          : 'Todos los favoritos están asignados.'}
                      </p>
                    )}
                  </div>

                  {/* ── Advanced params ── */}
                  <details className="border rounded-lg">
                    <summary className="px-4 py-3 cursor-pointer text-sm font-semibold text-gray-700 hover:bg-gray-50">
                      Parámetros avanzados
                    </summary>
                    <div className="px-4 pb-4 pt-2 grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs">Jornada laboral (horas)</Label>
                        <Input type="number" step="0.5" min={4} max={12} value={optConfig.jornada_horas}
                          onChange={e => setOptConfig({ ...optConfig, jornada_horas: parseFloat(e.target.value) })} />
                        <p className="text-xs text-gray-400 mt-1">Duración máxima ida + recogidas + vuelta</p>
                      </div>
                      <div>
                        <Label className="text-xs">Tiempo por parada (min)</Label>
                        <Input type="number" min={3} max={30} value={optConfig.tiempo_parada_min}
                          onChange={e => setOptConfig({ ...optConfig, tiempo_parada_min: parseInt(e.target.value) })} />
                        <p className="text-xs text-gray-400 mt-1">Minutos estimados por recogida</p>
                      </div>
                    </div>
                  </details>
                </div>
              )}

              {/* ── STEP 2: Results ── */}
              {optStep === 2 && (
                <div className="space-y-6">
                  {/* Summary */}
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <h3 className="font-semibold text-lg text-green-800 mb-2">Optimización completada</h3>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600">Camiones:</span>
                        <p className="font-bold text-xl">{optimizationResults.length}</p>
                      </div>
                      <div>
                        <span className="text-gray-600">Total tickets:</span>
                        <p className="font-bold text-xl">{optimizationResults.reduce((s, r) => s + r.tickets.length, 0)}</p>
                      </div>
                      <div>
                        <span className="text-gray-600">Distancia total est.:</span>
                        <p className="font-bold text-xl">
                          {optimizationResults.reduce((s, r) => s + (r.optimization?.distancia_estimada_km || 0), 0).toFixed(1)} km
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Per-truck results */}
                  {optimizationResults.map((result, idx) => (
                    <Card key={idx} className="border-l-4 border-l-blue-500" data-testid={`opt-result-${idx}`}>
                      <CardHeader className="pb-3">
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-base flex items-center gap-2">
                            <span className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center font-bold text-blue-700 text-sm">
                              {idx + 1}
                            </span>
                            {result.truck.vehiculo.matricula}
                            <span className="text-gray-400 font-normal text-sm">{result.truck.vehiculo.tipo || ''}</span>
                            {result.truck.conductor && <span className="text-gray-500 font-normal text-sm">— {result.truck.conductor}</span>}
                          </CardTitle>
                          <div className="flex gap-3 text-sm text-gray-600">
                            <span>{result.tickets.length} recogidas</span>
                            <span>{result.optimization?.tiempo_estimado_minutos || '~'} min</span>
                            <span>{result.optimization?.distancia_estimada_km?.toFixed(1) || '~'} km</span>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        {result.optimization?.razonamiento && (
                          <p className="text-sm text-blue-800 bg-blue-50 rounded p-3 mb-3">{result.optimization.razonamiento}</p>
                        )}
                        <div className="border rounded-lg divide-y max-h-52 overflow-y-auto">
                          {result.tickets.map((ticket, tIdx) => (
                            <div key={ticket.ticket_id} className="p-2 flex items-center gap-3 text-sm">
                              <span className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center text-xs font-bold text-gray-600 flex-shrink-0">
                                {tIdx + 1}
                              </span>
                              <span className="font-medium text-blue-700 w-24 flex-shrink-0">{ticket.ticket_id}</span>
                              <span className="text-gray-700 truncate flex-1">{ticket.direccion}</span>
                              <Badge variant="outline" className="text-xs flex-shrink-0">{ticket.lugar}</Badge>
                              <span className="text-gray-400 text-xs flex-shrink-0">{ticket.items?.length || 0} items</span>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>

            {/* Modal footer */}
            <div className="px-6 py-4 border-t bg-gray-50 flex justify-between items-center">
              {optStep === 1 ? (
                <>
                  <Button variant="outline" onClick={closeOptimizeModal}>Cancelar</Button>
                  <Button onClick={handleOptimize} disabled={optimizing || optTrucks.length === 0 || previewTickets.length < 2}
                    className="bg-blue-600 hover:bg-blue-700" data-testid="btn-run-optimization">
                    {optimizing ? 'Optimizando...' : `Optimizar ${optTrucks.length} camión(es) · ${previewTickets.length} tickets`}
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="outline" onClick={() => setOptStep(1)}>← Volver a configurar</Button>
                  <Button onClick={handleCreateAllRoutes} disabled={creatingRoutes}
                    className="bg-green-600 hover:bg-green-700 text-white" data-testid="btn-create-all-routes">
                    {creatingRoutes ? 'Creando...' : `Crear ${optimizationResults.filter(r => r.tickets.length > 0).length} ruta(s)`}
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════
          MANUAL CREATE MODAL
          ══════════════════════════════════════════════════════ */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowCreateModal(false)}>
          <div className="bg-white rounded-lg p-6 max-w-3xl w-full max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <h2 className="text-2xl font-bold mb-4" data-testid="create-modal-title">
              Nueva Ruta {ticketZoneFilter ? `- ${ticketZoneFilter}` : 'Manual'}
            </h2>
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Fecha</Label>
                  <Input type="date" value={formData.fecha} onChange={e => setFormData({ ...formData, fecha: e.target.value })} />
                </div>
                <div className="col-span-2">
                  <VehicleSelector onSelect={selectVehiculo} searchVal={vehiculoSearch} onSearchChange={setVehiculoSearch} />
                </div>
              </div>
              <div>
                <Label>Conductor (opcional)</Label>
                <Input value={formData.conductor} onChange={e => setFormData({ ...formData, conductor: e.target.value })} placeholder="Nombre" />
              </div>
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-base font-semibold">Tickets ({selectedTickets.length})</Label>
                </div>
                <div className="flex gap-3 mb-3">
                  <div className="flex-1">
                    <Label className="text-xs">Fecha recogida</Label>
                    <Input type="date" value={ticketDateFilter} onChange={e => setTicketDateFilter(e.target.value)} className="h-9" />
                  </div>
                  <div className="w-40">
                    <Label className="text-xs">Zona</Label>
                    <select value={ticketZoneFilter} onChange={e => setTicketZoneFilter(e.target.value)} className="w-full p-2 border rounded-md text-sm h-9">
                      <option value="">Todas</option>
                      <option value="Casco Antiguo">Casco Antiguo</option>
                      <option value="Puerta Tierra">Puerta Tierra</option>
                    </select>
                  </div>
                  <div className="flex items-end">
                    {availableTickets.length > 0 && (
                      <Button size="sm" variant="outline" onClick={() => {
                        const ids = availableTickets.map(t => t.ticket_id);
                        setSelectedTickets(prev => ids.every(id => prev.includes(id)) ? prev.filter(id => !ids.includes(id)) : [...new Set([...prev, ...ids])]);
                      }}>
                        {availableTickets.every(t => selectedTickets.includes(t.ticket_id)) ? 'Deseleccionar' : 'Seleccionar todos'}
                      </Button>
                    )}
                  </div>
                </div>
                <div className="border rounded-lg max-h-72 overflow-y-auto">
                  {availableTickets.length === 0 ? (
                    <div className="text-center py-8 text-gray-500 text-sm">No hay tickets pendientes</div>
                  ) : availableTickets.map(ticket => {
                    const sel = selectedTickets.includes(ticket.ticket_id);
                    return (
                      <div key={ticket._id} className={`flex items-center gap-3 p-3 border-b last:border-b-0 cursor-pointer transition-colors duration-100 ${sel ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                        onClick={() => toggleTicket(ticket.ticket_id)}>
                        <input type="checkbox" checked={sel} onChange={() => {}} className="rounded pointer-events-none" />
                        <div className="flex-1 min-w-0">
                          <span className="font-medium text-sm">{ticket.ticket_id}</span>
                          <span className="text-xs text-gray-500 ml-2">{ticket.nombre_ciudadano}</span>
                          <p className="text-xs text-gray-600 truncate">{ticket.direccion}</p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <Badge variant="outline" className="text-xs">{ticket.lugar}</Badge>
                          <p className="text-xs text-gray-400">{ticket.items?.length || 0} items</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowCreateModal(false)}>Cancelar</Button>
              <Button onClick={handleCreateRuta} disabled={!formData.vehiculo_id || selectedTickets.length === 0}>
                Crear Ruta ({selectedTickets.length} tickets)
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminRutas;
